<template>
  <SoDataTable
    :headers="issuesTableHeaders"
    :items="issuesList"
    :loading="isLoading"
    :search-placeholder="'Search within incident'"
    :no-data-text="'No results were found for the applied criteria'"
    row-clickable
    @click:row="onRowSelected"
  >
    <template #tableActions>
      <SoCol :default-width="5" class="py-0 pr-0">
        <SoSelect
          v-model="defaultSystemOptions"
          :items="systemOptions"
          type="multiple"
          placeholder="Select system"
          class="mb-3"
        />
        <SoSelect
          v-model="defaultStatuses"
          :items="statusOptions"
          type="multiple"
          placeholder="Select status"
        />
      </SoCol>
    </template>
    <template #item.priorityBadge="{ internalItem }">
      <v-img
        :src="internalItem.raw['priorityBadge']"
        :width="24"
        aspect-ratio="1"
      />
    </template>
    <template #item.statusBadge="{ internalItem }">
      <v-img
        :src="internalItem.raw['statusBadge']"
        :width="24"
        aspect-ratio="1"
      />
    </template>
    <template #item.status="{ internalItem }">
      <SoBadge ref="badge" :color="internalItem.raw['statusColor']">
        {{ internalItem.raw["status"] }}
      </SoBadge>
    </template>
  </SoDataTable>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import {
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import dayjs from "dayjs"
  import { daysAgoFormatted } from "@/helpers/dateHelper"
  import SoBadge from "@/components/SoBadge.vue"
  import {
    IssuesStatusOptions,
    IssuesSystemOptions,
    DefaultIssuesStatusOptions,
    DefaultIssuesSystemOptions,
  } from "@/constants/IssuesFilterOptions"
  import SoCol from "@/components/SoCol.vue"
  import { JiraIssuesService } from "@/services/JiraIssuesService"

  const headers: SoDataTableHeader[] = [
    {
      title: "Ticket",
      key: "key",
      sortable: false,
    },
    {
      title: "",
      key: "priorityBadge",
      sortable: false,
    },
    {
      title: "Priority",
      key: "priority",
      sortable: true,
    },
    {
      title: "",
      key: "statusBadge",
      sortable: false,
    },
    {
      title: "Status",
      key: "status",
      sortable: false,
    },
    {
      title: "System",
      key: "system",
      sortable: false,
    },
    {
      title: "Summary",
      key: "summary",
      sortable: false,
    },
    {
      title: "Created",
      key: "created",
      sortable: false,
    },
    {
      title: "",
      key: "days",
      sortable: false,
    },
  ]

  export default defineComponent({
    components: {
      SoCol,
      SoBadge,
      SoSelect,
      SoDataTable,
    },
    data() {
      return {
        issuesTableHeaders: headers,
        systemOptions: IssuesSystemOptions,
        defaultSystemOptions: DefaultIssuesSystemOptions,
        statusOptions: IssuesStatusOptions,
        defaultStatuses: DefaultIssuesStatusOptions,
        issuesService: JiraIssuesService.getJiraIssues,
      }
    },
    computed: {
      getIssues() {
        return this.issuesService({
          status: this.defaultStatuses,
          system: this.defaultSystemOptions,
        })
      },
      issues() {
        return this.getIssues?.data?.issues
      },
      isLoading() {
        return this.getIssues?.isFetching
      },
      issuesList() {
        if (!this.issues?.length) return []
        return this.issues?.map((issue) => {
          return {
            key: issue.key,
            summary: issue.fields.summary,
            status: issue.status?.text,
            system: issue.fields.customfield_10092?.value,
            priority: issue.fields.priority?.name,
            priorityBadge: issue.fields.priority?.iconUrl,
            statusBadge: issue.fields.status?.iconUrl,
            statusColor: issue.status?.type,
            created: dayjs(issue.fields.created).format("Do MMM YY"),
            days: daysAgoFormatted(issue.fields.created),
          } as SoDataTableItem
        })
      },
    },
    methods: {
      async onRowSelected(item: SoDataTableItem) {
        if (item) {
          await this.$router.push({
            path: "/issues/" + item.key,
          })
        }
      },
    },
  })
</script>
